import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InputTourModelReleaseFormV1,
  TourDataWizardMrfFragment,
  useGetWizardMrfDataQuery,
  useUpdateWizardMrfDataMutation,
} from '../../../generated/graphql';
import { routes } from '../../../routes/routesConfig';
import { extractFromUnion } from '../../../utils/extractor';
import { WizardParentModalLayout } from '../components/WizardParentModalLayout/WizardParentModalLayout';
import { WizardStepSwitcher } from '../components/WizardStepSwitcher/WizardStepSwitcher';
import {
  useWizardCloseCallback,
  useWizardNavigateToNextStepCallback,
} from '../utils/hooks';
import { extractCurrentFieldError, extractTourStepField } from '../utils/utils';
import {
  WizardMRFContext,
  WizardMRFContextProvider,
  paperlessMrfRedirectUrl,
  possibleStepIds,
} from './WizardMRFContext';
import { WizardMRFModelReleaseFormV1Finish } from './step/ModelReleaseFormV1Finish/WizardMRFModelReleaseFormV1Finish';
import { WizardMRFModelReleaseFormV1FullAddress } from './step/ModelReleaseFormV1FullAddress/WizardMRFModelReleaseFormV1FullAddress';
import { WizardMRFModelReleaseFormV1PersonalData } from './step/ModelReleaseFormV1PersonalData/WizardMRFModelReleaseFormV1PersonalData';
import { WizardMRFModelReleaseFormV1Signing } from './step/ModelReleaseFormV1Signing/WizardMRFModelReleaseFormV1Signing';
import { WizardMRFModelReleaseFormV1Start } from './step/ModelReleaseFormV1Start/WizardMRFModelReleaseFormV1Start';

export const wizardMRFSteps: WizardMRFContext.StepsRouteConfig = {
  ModelReleaseFormV1Start: {
    routeConfig: routes.wizardMRFWelcome,
    element: <WizardMRFModelReleaseFormV1Start />,
  },
  ModelReleaseFormV1PersonalData: {
    routeConfig: routes.wizardMRFAboutMe,
    element: <WizardMRFModelReleaseFormV1PersonalData />,
  },
  ModelReleaseFormV1FullAddress: {
    routeConfig: routes.wizardMRFAddress,
    element: <WizardMRFModelReleaseFormV1FullAddress />,
  },
  ModelReleaseFormV1Signing: {
    routeConfig: routes.wizardMRFToPaperless,
    element: <WizardMRFModelReleaseFormV1Signing />,
  },
  ModelReleaseFormV1Finish: {
    routeConfig: routes.wizardMRFFinished,
    element: <WizardMRFModelReleaseFormV1Finish />,
  },
};

type State = {
  totalSteps: number;
  currentStep: WizardMRFContext.Step | null;
  currentField: Partial<WizardMRFContext.Field>;
  currentFieldError: Partial<WizardMRFContext.FieldError>;
} & WizardMRFContext.Addition;

export const WizardMRF: React.FC = () => {
  const { t } = useTranslation(['wizardMRF', 'validation']);

  const [state, setState] = useState<State>({
    totalSteps: possibleStepIds.length,
    currentStep: null,
    currentField: {},
    currentFieldError: {},
    accountPrefillField: {},
  });
  const { totalSteps, currentStep } = state;

  const wizardCloseCallback = useWizardCloseCallback();
  const navigateToNextWizardStepCallback =
    useWizardNavigateToNextStepCallback(wizardMRFSteps);

  const setTourStepData = useCallback(
    (
      tourData?: TourDataWizardMrfFragment | null,
      addition?: Partial<WizardMRFContext.Addition>
    ) => {
      const step = tourData?.currentStep ?? null;

      setState((prevState) => ({
        ...prevState,
        totalSteps: tourData?.totalSteps ?? possibleStepIds.length,
        currentStep: step,
        currentField: extractTourStepField(step?.fields),
        currentFieldError: extractCurrentFieldError(
          tourData?.validate?.data,
          t('validation:error.UnbekannterFehler')
        ),
        ...addition,
      }));

      if (tourData) {
        navigateToNextWizardStepCallback(tourData.currentStep?.id ?? null);
      }
    },
    [navigateToNextWizardStepCallback, t]
  );

  const { loading } = useGetWizardMrfDataQuery({
    variables: {
      redirectUrl: paperlessMrfRedirectUrl,
    },
    onCompleted: (data) => {
      if (data?.tour?.__typename !== 'TourModelReleaseFormV1') {
        return;
      }

      const accountPrefillField =
        extractFromUnion(
          data?.profile?.collection,
          'ProfileCollectionAccountV1'
        )?.fields ?? null;

      setTourStepData(data?.tour, { accountPrefillField });
    },
  });

  const [updateStepData] = useUpdateWizardMrfDataMutation({
    onCompleted: (data) => {
      if (data?.tour?.__typename !== 'TourModelReleaseFormV1Mutation') {
        return;
      }
      setTourStepData(data?.tour?.update);
    },
  });

  const wizardNextStepCallback = React.useCallback(
    async (fields?: InputTourModelReleaseFormV1) => {
      if (!currentStep?.id) {
        return;
      }
      await updateStepData({
        variables: {
          step: currentStep.id,
          data: fields ?? {},
          redirectUrl: paperlessMrfRedirectUrl,
        },
      });
    },
    [updateStepData, currentStep]
  );

  const context = React.useMemo(() => {
    return {
      ...state,
      wizardCloseCallback,
      wizardNextStepCallback,
    };
  }, [state, wizardCloseCallback, wizardNextStepCallback]);

  return (
    <WizardParentModalLayout
      headerText={t('wizardMRF:heading.ModelReleaseForm')}
      totalSteps={totalSteps}
      currentStep={currentStep?.pos ?? 1}
      loading={loading}
      onCloseClick={wizardCloseCallback}
    >
      <WizardMRFContextProvider value={context}>
        <WizardStepSwitcher
          steps={wizardMRFSteps}
          onBackNavigation={wizardCloseCallback}
        />
      </WizardMRFContextProvider>
    </WizardParentModalLayout>
  );
};
